import { CloseIcon } from '@assets/icons/CloseIcon';
import { useEntitlements } from '@hooks/useEntitlements';
import { usePreferences } from '@hooks/usePreferences';
import type { RadioChangeEvent } from 'antd';
import { Modal, Radio } from 'antd';
import classNames from 'classnames';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useMatch } from 'react-router';
import { Amplitude } from 'src/amplitude';
import { SupportedEntitlements } from '../../core/models/entitlements';
import { saveUserPreferences } from '../../redux/user/thunks';
import { routePaths } from '../../screens/routes';
import { BaseButton } from '../shared/Buttons';
import { PaletteItem } from '../shared/Palette';
import { SaveButton } from './FooterButtons';

const RadioGroup = Radio.Group;

export const PalletModal = ({ visible, close }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const entitlements = useEntitlements();
  const { colorMaps, selectedColorMap, selectedIndex } = usePreferences();

  const isOnAnalytics = useMatch(routePaths.analytics);

  const hasAccessToCustomColors = entitlements.has(SupportedEntitlements.MAPVIEW_CUSTOMCOLOR);

  const [paletteIndex, setPaletteIndex] = useState(
    colorMaps.findIndex((item) => item.name === selectedColorMap.name) ?? 0
  );

  const onChange = useCallback((event: RadioChangeEvent) => {
    setPaletteIndex(event.target.value);
  }, []);

  const onSubmit = () => {
    const newPalette = colorMaps[paletteIndex];
    if (newPalette) {
      dispatch(saveUserPreferences({ selectedColorMap: newPalette }));
      Amplitude.trackChangeSelectedPalette(newPalette.name, false);
    }
    close();
  };

  const footer = (
    <div className="footerButtons">
      <BaseButton onClick={() => close()} size="large" variant="cancel" key="cancel">
        {t('common.cancel')}
      </BaseButton>
      <SaveButton
        key="save"
        onSubmit={onSubmit}
        confirmLoading={false}
        text={t('common.save')}
        className="palette-modal__save"
      />
    </div>
  );

  return (
    <Modal
      visible={visible}
      onCancel={close}
      className={classNames('palette-modal__palette', {
        newDesign: !isOnAnalytics,
      })}
      closable={true}
      footer={footer}
      closeIcon={<CloseIcon className="inline-block" />}
      title={t('map.change_palette.title')}
    >
      <RadioGroup onChange={onChange} value={paletteIndex}>
        {colorMaps.map((color, index) => {
          return !hasAccessToCustomColors && index > 1 ? null : (
            <Radio
              className="palette-radio"
              value={index}
              key={color.name}
              data-testid={`palette-radio-${color.name}`}
            >
              <span className="palette-radio__label">{color.name}</span>
              <PaletteItem
                palette={color}
                width={600}
                noGrayScale={selectedIndex === 'shadow_corrected_drymatter'}
              />
            </Radio>
          );
        })}
      </RadioGroup>
    </Modal>
  );
};

export default PalletModal;
